/**
 * 69.9 step 5 — the bots and the commander learning the mounts (the coordinator's brief: "the strike rule weighs a known
 * enemy mount (expected losses, saturating it, or two strikes back to back); gun crews and rocketeers put the radar head
 * first when they see it; ships don't land under a known mount (the landing's site choice reads it). Within the knowledge
 * model: only mounts the side has seen"). What a side knows of the enemy's point defence is what its eyes saw: once a second
 * (match.ts, with pointDefence on and a mount standing) each side's living people, flying drones and standing towers look
 * for the enemy's mounts as they look for a ship landing (core/landingIntel.ts teamSeesShipAt: the weather's sight, a line
 * of sight to the mount's radar head); a mount seen is in `state.mountsSeen[team]` with the tick, kept while it stands —
 * a mount does not move, and its loss is seen the same way: a remembered mount no longer standing is dropped from the list
 * only when the side has sight of its radar head's place (or sees it go), never from the truth (design-3's catch, Oct 21:
 * it was dropped the second it died, wherever the side's eyes were). Till then the side believes it stands.
 * Readers: `knownMounts` (the enemy's mounts this side has seen and has not seen lost — standing or not, as it believes), `underKnownMount` (the known mount whose gun's
 * reach covers a surface point), `shareThrough` (the share of a strike's rounds expected past the known mounts over a
 * point). Nothing without the switch: no mount, no record, every byte as before.
 * 69.50 step 2b: **a site's generator** is learnt the same way (its enclosure's top in sight), and its loss is kept, not
 * dropped (`lost`): a known site whose generator the side has seen down is dark — not in `knownMounts`, not cover — while
 * one whose generator fell unseen is still believed live.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { POINT_DEFENCE, MOUNT_INTEL } from '../constants/action';
import { structureTeam } from './body';
import { teamSeesShipAt } from './landingIntel';
import { radarEye, isSite, pdOn } from './pointDefence';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personSurfaceRadius } from './fineTerrain';
import { dacos } from './dmath';
import { vecAdd, vecDot, vecNormalize, vecScale } from '../math';
import { towedGunsOn } from './defaultOn';

const arcPeople = (state: GameState, pi: number, a: Vec3, b: Vec3): number =>
  dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[pi].radius / (PERSON_BODY_HEIGHT * personSetOf(state).k);

/** The enemy's standing mounts (not this side's). */
export function enemyMounts(state: GameState, team: number): StructureState[] {
  return state.structures.filter(s => s.type === 'pointDefence' && s.health > 0 && structureTeam(state, s) !== team).sort((a, b) => a.id - b.id);
}

/** 69.50 step 2b: the enemy's sites' generators (standing or not), in id order. */
function enemyGenerators(state: GameState, team: number): StructureState[] {
  return state.structures.filter(s => s.type === 'generator' && structureTeam(state, s) !== team).sort((a, b) => a.id - b.id);
}
/** Where a side's eyes look for a generator: its enclosure's top (a person up). */
function generatorEye(state: GameState, g: StructureState): Vec3 {
  const planet = state.planets[g.planetIndex], ps = personSetOf(state), dir = vecNormalize(g.surfaceDir);
  return vecAdd(planet.position, vecScale(dir, personSurfaceRadius(planet, dir, ps) + PERSON_BODY_HEIGHT * ps.k));
}

/** Once a second (match.ts), with the point defence on: each side's seen mounts refreshed; the same state when nothing changed. */
export function stepMountIntel(state: GameState, tick: number): GameState {
  if (!pdOn(state.config) || !state.structures.some(s => s.type === 'pointDefence')) return state;   // (69.50 step 2a: the forward sites are learnt so too)
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  let changed = false;
  const out: NonNullable<GameState['mountsSeen']> = {};
  for (const team of teams) {
    const all = state.mountsSeen?.[team] ?? [];
    const isGen = (id: number) => state.structures.find(s => s.id === id)?.type === 'generator';
    const was = all.filter(e => !isGen(e.id));
    const standing = enemyMounts(state, team);
    // (a mount it remembers that no longer stands: dropped when its eyes are on the place, else kept — it does not know)
    let list = was.filter(e => {
      if (standing.some(m => m.id === e.id)) return true;
      const m = state.structures.find(s => s.id === e.id);
      return !!m && !teamSeesShipAt(state, team, m.planetIndex, radarEye(state, m), tick);
    });
    for (const m of standing) {
      if (!teamSeesShipAt(state, team, m.planetIndex, radarEye(state, m), tick)) continue;
      const i = list.findIndex(e => e.id === m.id);
      if (i >= 0) list = list.map((e, k) => (k === i ? { ...e, tick } : e)); else list = [...list, { id: m.id, tick }];
    }
    // 69.50 step 2b: the generators — seen standing, or seen down (kept, marked lost: its site known dark)
    const gens = all.filter(e => isGen(e.id));
    for (const g of enemyGenerators(state, team)) {
      if (!teamSeesShipAt(state, team, g.planetIndex, generatorEye(state, g), tick)) continue;
      const entry = g.health > 0 ? { id: g.id, tick } : { id: g.id, tick, lost: true as const }, i = gens.findIndex(e => e.id === g.id);
      if (i >= 0) gens[i] = entry; else gens.push(entry);
    }
    list = [...list, ...gens].sort((a, b) => a.id - b.id);
    if (list.length !== all.length || list.some((e, k) => e.id !== all[k].id || e.tick !== all[k].tick || e.lost !== all[k].lost)) changed = true;
    if (list.length) out[team] = list;
  }
  if (!changed) return state;
  if (!Object.keys(out).length) { const { mountsSeen: _m, ...rest } = state; return rest as GameState; }
  return { ...state, mountsSeen: out };
}

/**
 * The enemy's mounts this side has seen and has not seen lost (in id order) — a wreck it has not looked at is one it believes
 * stands. 69.50 step 2b: a site whose generator the side has seen down is dark, so not among them.
 */
export function knownMounts(state: GameState, team: number): StructureState[] {
  const seen = state.mountsSeen?.[team];
  if (!seen?.length) return [];
  const dark = (m: StructureState) => m.pd?.power !== undefined && seen.some(e => e.id === m.pd!.power && e.lost);
  return state.structures.filter(m => m.type === 'pointDefence' && structureTeam(state, m) !== team && seen.some(e => e.id === m.id) && !dark(m)).sort((a, b) => a.id - b.id);
}

/** The mount's gun reach (people). */
export const mountReach = (m: Pick<StructureState, 'pd'>): number => POINT_DEFENCE.variants[m.pd?.variant ?? 'cram'].reach;

/**
 * The known enemy mount whose gun's reach covers the surface point `dir` on `planetIndex` (the nearest when several), or null.
 * 69.50 step 2a: an air-defence site's reach is its missile's — known cover against ships (a landing, a route); `rounds`
 * asks only what stops rounds (the strike rule's question), which a site does not.
 */
export function underKnownMount(state: GameState, team: number, planetIndex: number, dir: Vec3, rounds = false): StructureState | null {
  let best: StructureState | null = null, bestD = Infinity;
  for (const m of knownMounts(state, team)) {
    if (m.planetIndex !== planetIndex || (rounds && isSite(m))) continue;
    const d = arcPeople(state, planetIndex, m.surfaceDir, dir);
    if (d <= mountReach(m) && d < bestD) { best = m; bestD = d; }
  }
  return best;
}

/** The share of a strike's rounds expected to get through the known enemy mounts whose reach covers `dir` (1: none known there). */
export function shareThrough(state: GameState, team: number, planetIndex: number, dir: Vec3): number {
  let share = 1;
  for (const m of knownMounts(state, team)) {
    if (m.planetIndex !== planetIndex || arcPeople(state, planetIndex, m.surfaceDir, dir) > mountReach(m)) continue;
    share *= 1 - (towedGunsOn(state.config) ? MOUNT_INTEL.hitShareWithGuns : MOUNT_INTEL.hitShare)[m.pd?.variant ?? 'cram'];   // (69.13 (c): the towed guns' tuning)
  }
  return share;
}
